/* Volatile — cart updates via /cart/change.js
 * Works for both the cart drawer and the /cart page. Document-level delegation
 * so any [data-cart] container (drawer or page) updates on each action.
 */
(() => {
  if (window.VolatileCartUiBound) return;
  window.VolatileCartUiBound = true;

  const roots = () => document.querySelectorAll('[data-cart]');
  if (roots().length === 0) return;

  const formatNumber = (cents) =>
    new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(cents / 100));
  const money = (cents) => `INR ${formatNumber(cents)}`;
  // Match `snippets/v-price.liquid` byte-for-byte so the same .v-price* CSS applies.
  const priceHtml = (cents) =>
    `<span class="v-price" data-price-cents="${cents}">` +
      `<span class="v-price__current">` +
        `<span class="v-price__cur">INR</span>` +
        `<span class="v-price__num">${formatNumber(cents)}</span>` +
      `</span>` +
    `</span>`;

  const flashMessage = (msg, err = false) => {
    const flashes = document.querySelectorAll('[data-cart-flash]');
    flashes.forEach((el) => {
      el.textContent = msg;
      el.hidden = false;
      el.style.color = err ? '#c00' : 'var(--ink-mute)';
    });
    setTimeout(() => flashes.forEach((el) => { el.hidden = true; }), 2800);
  };

  const renderAll = (cart) => {
    document.querySelectorAll('[data-cart-subtotal]').forEach((el) => {
      el.innerHTML = priceHtml(cart.items_subtotal_price);
    });
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = `(${cart.item_count})`;
    });
    document.querySelectorAll('[data-header-cart-count]').forEach((el) => {
      el.textContent = String(cart.item_count);
      el.hidden = cart.item_count === 0;
    });

    // Auto-close the drawer the moment the cart is empty — no point leaving
    // a "0 items" sheet hanging.
    if (cart.item_count === 0) {
      const dlg = document.querySelector('[data-cart-drawer]');
      if (dlg && dlg.open && typeof dlg.close === 'function') dlg.close();
    }

    document.querySelectorAll('[data-cart]').forEach((root) => {
      const rows = root.querySelectorAll('[data-line]');
      rows.forEach((row) => {
        const line = Number(row.dataset.line);
        const item = cart.items[line - 1];
        if (!item) {
          row.remove();
        } else {
          const qtyEl = row.querySelector('[data-cart-qty]');
          const priceEl = row.querySelector('[data-line-price]');
          const totalEl = row.querySelector('[data-line-total]');
          if (qtyEl) qtyEl.value = item.quantity;
          if (priceEl) priceEl.innerHTML = priceHtml(item.final_price);
          if (totalEl) totalEl.innerHTML = priceHtml(item.final_line_price);
        }
      });

      const emptyEls = root.querySelectorAll('[data-cart-empty]');
      const formEls = root.querySelectorAll('[data-cart-form], [data-cart-items], [data-cart-foot]');
      emptyEls.forEach((el) => { el.hidden = cart.item_count > 0; });
      formEls.forEach((el) => { el.hidden = cart.item_count === 0; });
    });
  };

  // Optimistic update: mutate qty + line total + subtotal + header count
  // in the DOM BEFORE the network round-trip. Server reconciles later
  // via renderAll once /cart/change.js returns.
  const bumpHeaderCount = (delta) => {
    document.querySelectorAll('[data-header-cart-count]').forEach((el) => {
      const cur = parseInt(el.textContent, 10) || 0;
      const next = Math.max(0, cur + delta);
      el.textContent = String(next);
      el.hidden = next === 0;
    });
  };
  const optimisticUpdate = (rowEl, newQty) => {
    if (!rowEl) return;
    const qtyEl = rowEl.querySelector('[data-cart-qty]');
    const oldQty = Number(qtyEl && qtyEl.value) || 0;
    bumpHeaderCount(newQty - oldQty);
    const totalEl = rowEl.querySelector('[data-line-total]');
    const numEl = totalEl && totalEl.querySelector('.v-price__num');
    // Cache the unit price on first run so we can recompute the line total
    // locally without re-asking the server. Each call is cheap.
    let unitCents = Number(rowEl.dataset.unitCents) || 0;
    if (!unitCents && numEl) {
      const baseQty = oldQty || 1;
      const lineCents = parseInt(numEl.textContent.replace(/[^\d]/g, ''), 10) * 100;
      if (lineCents && baseQty) {
        unitCents = Math.round(lineCents / baseQty);
        rowEl.dataset.unitCents = String(unitCents);
      }
    }
    if (qtyEl) qtyEl.value = newQty;
    if (newQty === 0) {
      /* Remove the row from layout immediately so the cart visibly
         shrinks the moment the user taps "Remove" — the server then
         reconciles via the section render (replaces innerHTML below).
         Setting display:none keeps the node in the DOM so the
         changeLine() error handler can revert if the request fails. */
      rowEl.classList.add('is-removing');
      rowEl.style.display = 'none';
    } else if (totalEl && unitCents) {
      totalEl.innerHTML = priceHtml(unitCents * newQty);
    }
    // Recompute subtotal locally by summing every visible line total.
    let subtotalCents = 0;
    document.querySelectorAll('[data-cart] [data-line]').forEach((r) => {
      if (r.classList.contains('is-removing')) return;
      const u = Number(r.dataset.unitCents) || 0;
      const q = Number((r.querySelector('[data-cart-qty]') || {}).value) || 0;
      subtotalCents += u * q;
    });
    if (subtotalCents) {
      document.querySelectorAll('[data-cart-subtotal]').forEach((el) => {
        el.innerHTML = priceHtml(subtotalCents);
      });
    }
    // Track lastQty so a subsequent stepper press can compute its delta
    // even after we mutated `qtyEl.value` earlier in this function.
    if (qtyEl) qtyEl.dataset.lastQty = String(newQty);

    /* If every visible row was just removed, flip the drawer into its
       empty state immediately so the user sees the "Your cart is empty"
       message instead of an empty list while the server reconciles. */
    document.querySelectorAll('[data-cart]').forEach((root) => {
      var visibleRows = Array.from(root.querySelectorAll('[data-line]'))
        .filter(function (r) { return r.style.display !== 'none' && !r.classList.contains('is-removing'); });
      if (visibleRows.length === 0) {
        root.querySelectorAll('[data-cart-empty]').forEach(function (el) { el.hidden = false; });
        root.querySelectorAll('[data-cart-form], [data-cart-items], [data-cart-foot]').forEach(function (el) { el.hidden = true; });
      }
    });
  };

  const changeLine = async (line, quantity, rowEl) => {
    // Optimistic UI first — feels instant. Snapshot the variant id from
    // the row so the shared state can reconcile without a second fetch.
    optimisticUpdate(rowEl, quantity);
    const variantId = rowEl ? Number(rowEl.dataset.variantId) || null : null;
    if (window.VolatileCart && variantId) {
      window.VolatileCart.optimisticChange(variantId, quantity);
    }
    try {
      const res = await fetch('/cart/change.js?sections=cart-drawer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cart = await res.json();
      renderAll(cart);
      // Authoritative server state → broadcast so every subscriber
      // (PDP cart-action, compact-sheet + button, visit-cart label,
      // header count) reconciles in one shot.
      if (window.VolatileCart) window.VolatileCart.setState(cart);
      // Replace the drawer's inner HTML so new rows and removed rows reflect.
      const drawerHtml = cart.sections && cart.sections['cart-drawer'];
      const drawerEl = document.querySelector('[data-cart-drawer]');
      if (drawerHtml && drawerEl) {
        const parser = new DOMParser();
        const fresh = parser.parseFromString(drawerHtml, 'text/html').querySelector('[data-cart-drawer]');
        if (fresh) drawerEl.innerHTML = fresh.innerHTML;
      }
      if (quantity === 0) flashMessage('Item removed from cart.');
    } catch (err) {
      flashMessage('Could not update cart. Please try again.', true);
      if (rowEl) {
        rowEl.classList.remove('is-removing');
        rowEl.style.display = '';
      }
      // Reconcile by re-fetching the truth — undoes any optimistic drift.
      if (window.VolatileCart) window.VolatileCart.refresh();
    }
  };

  // Event delegation at document level, so it works in drawer + page
  document.addEventListener('click', (e) => {
    const inc = e.target.closest('[data-cart-increase]');
    const dec = e.target.closest('[data-cart-decrease]');
    const rem = e.target.closest('[data-cart-remove]');
    const row = e.target.closest('[data-line]');
    if (!row) return;
    if (!row.closest('[data-cart]')) return;

    const line = Number(row.dataset.line);
    const qtyEl = row.querySelector('[data-cart-qty]');
    const qty = qtyEl ? Number(qtyEl.value) : 1;

    if (inc) changeLine(line, qty + 1, row);
    else if (dec) changeLine(line, Math.max(0, qty - 1), row);
    else if (rem) changeLine(line, 0, row);
  });

  document.addEventListener('change', (e) => {
    const qtyEl = e.target.closest('[data-cart-qty]');
    if (!qtyEl) return;
    const row = e.target.closest('[data-line]');
    if (!row || !row.closest('[data-cart]')) return;
    const line = Number(row.dataset.line);
    const newQty = Math.max(0, Number(qtyEl.value) || 0);
    changeLine(line, newQty, row);
  });

  // Note textarea (drawer + page)
  document.querySelectorAll('[data-cart-note]').forEach((el) => {
    let t;
    el.addEventListener('input', () => {
      clearTimeout(t);
      t = setTimeout(async () => {
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: el.value }),
          });
        } catch (_) {}
      }, 400);
    });
  });
})();
